import { useEffect, useState } from 'react';
import { CircularProgress, makeStyles, Typography } from '@material-ui/core';
import { Link } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { visitsApiRef } from '@backstage/plugin-home';
import { Entity } from '@backstage/catalog-model';
import { cockpitTokens } from './cockpitTokens';
import { useHomeSources } from './homeSources';
import { ciVerdictOf, slugOf } from './ciSnapshot';
import { updatedLabel } from './updatedLabel';
import { ListRow, toRow } from '../catalog/list/listFilters';
import { LifecycleOf, OwnerOf } from '../catalog/list/rowParts';

/**
 * Recently used (DX-307): the components the reader opened last, newest first —
 * "where you left off", from the same visit history the palette's Recent reads
 * (DX-349).
 *
 * The history is the browser's: it is kept per reader and per browser, so a
 * colleague on a second machine starts this table empty. It names entities by
 * their ref, so the catalog is asked once for all of them — for what a row
 * shows, and because a component deleted since it was opened is not something
 * to offer; the catalog not answering for a ref is what drops it.
 *
 * The draft's Health column is the build here, and is called CI: nothing in the
 * portal measures whether a service is up (specs/home-page.md AC-25).
 *
 * MUI v4 only.
 */

/** How many rows the table shows — the draft's four, and one for luck. */
export const RECENT_ROWS = 5;

const FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'metadata.description',
  'metadata.annotations',
  'metadata.tags',
  'spec.owner',
  'spec.type',
  'spec.lifecycle',
  'spec.system',
  'relations',
];

type Opened = { row: ListRow; openedAt: number };

const useStyles = makeStyles(theme => ({
  /* The title in the card's head, as the attention card and My workspace
     carry theirs; it stood above the card at first. */
  head: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${cockpitTokens.color.border}`,
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: 600,
    color: cockpitTokens.color.text,
  },
  all: {
    fontSize: '0.82rem',
    color: cockpitTokens.color.textMuted,
  },
  card: {
    background: cockpitTokens.color.surface,
    border: `1px solid ${cockpitTokens.color.border}`,
    borderRadius: cockpitTokens.radius.card,
    boxShadow: cockpitTokens.shadow.card,
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    tableLayout: 'fixed' as const,
    borderCollapse: 'collapse' as const,
    '& th': {
      textAlign: 'left' as const,
      fontSize: '0.75rem',
      fontWeight: 500,
      color: cockpitTokens.color.textFaint,
      padding: theme.spacing(1, 2),
      borderBottom: `1px solid ${cockpitTokens.color.border}`,
    },
    '& td': {
      padding: theme.spacing(1.25, 2),
      borderBottom: `1px solid ${cockpitTokens.color.border}`,
      fontSize: '0.85rem',
      color: cockpitTokens.color.text,
      verticalAlign: 'middle' as const,
      overflow: 'hidden',
    },
    '& tr:last-child td': { borderBottom: 'none' },
  },
  /* Owner and lifecycle give way first on a narrow screen: the service, its
     build and when it was opened are what the row is for. */
  wide: {
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  // Sized for the attention card's column, two thirds of the page: the CI and
  // lifecycle columns hold their longest words ("No repository",
  // "experimental") without being cut; a long owner wraps instead.
  colOwner: { width: '18%' },
  colLifecycle: { width: '16%' },
  colCi: { width: '18%' },
  colOpened: {
    width: '14%',
    textAlign: 'right' as const,
    [theme.breakpoints.down('xs')]: { width: '26%' },
  },
  service: {
    display: 'block',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  description: {
    display: 'block',
    fontSize: '0.78rem',
    color: cockpitTokens.color.textMuted,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  verdict: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap' as const,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
    background: cockpitTokens.color.textFaint,
  },
  failing: { color: theme.palette.error.main },
  passing: { color: theme.palette.success.main },
  notKnown: { color: cockpitTokens.color.textFaint },
  dotFailing: { background: theme.palette.error.main },
  dotPassing: { background: theme.palette.success.main },
  opened: {
    color: cockpitTokens.color.textMuted,
    whiteSpace: 'nowrap' as const,
  },
  said: {
    padding: theme.spacing(2),
    fontSize: '0.85rem',
    color: cockpitTokens.color.textMuted,
  },
}));

const CiCell = ({ slug }: { slug?: string }) => {
  const classes = useStyles();
  const { ci } = useHomeSources();
  const verdict = ciVerdictOf(slug, ci);
  if (verdict.kind === 'loading') return <CircularProgress size={12} />;
  const tone = {
    failing: classes.failing,
    passing: classes.passing,
    'not-known': classes.notKnown,
  }[verdict.kind];
  const dot = {
    failing: classes.dotFailing,
    passing: classes.dotPassing,
    'not-known': '',
  }[verdict.kind];
  return (
    <span
      className={`${classes.verdict} ${tone}`}
      title={verdict.hint}
      data-verdict={verdict.kind}
    >
      <span className={`${classes.dot} ${dot}`} aria-hidden />
      {verdict.label}
    </span>
  );
};

export const RecentlyUsed = () => {
  const classes = useStyles();
  const visitsApi = useApi(visitsApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [opened, setOpened] = useState<Opened[] | 'loading' | 'failed'>(
    'loading',
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const visits = await visitsApi.list({
          limit: 100,
          orderBy: [{ field: 'timestamp', direction: 'desc' }],
        });
        // Once per component, however its ref was spelled, keeping the most
        // recent opening — the list is newest first, so the first one seen.
        const when = new Map<string, number>();
        const refs: string[] = [];
        for (const v of visits) {
          const ref = v.entityRef?.toLocaleLowerCase('en-US');
          if (!ref || !ref.startsWith('component:') || when.has(ref)) continue;
          when.set(ref, v.timestamp);
          refs.push(v.entityRef!);
        }
        // A few more than are shown, so a deleted component does not leave the
        // table a row short.
        const wanted = refs.slice(0, RECENT_ROWS * 2);
        if (wanted.length === 0) {
          if (alive) setOpened([]);
          return;
        }
        const { items } = await catalogApi.getEntitiesByRefs({
          entityRefs: wanted,
          fields: FIELDS,
        });
        const rows = items
          .map((entity, i) =>
            entity
              ? {
                  row: toRow(entity as Entity),
                  openedAt: when.get(wanted[i].toLocaleLowerCase('en-US')) ?? 0,
                }
              : undefined,
          )
          .filter((o): o is Opened => Boolean(o))
          .slice(0, RECENT_ROWS);
        if (alive) setOpened(rows);
      } catch {
        if (alive) setOpened('failed');
      }
    })();
    return () => {
      alive = false;
    };
  }, [visitsApi, catalogApi]);

  return (
    <section className={classes.card} aria-labelledby="recently-used-title">
      <div className={classes.head}>
        <h2 id="recently-used-title" className={classes.title}>
          Recently used
        </h2>
        <Link to="/catalog?filters%5Bkind%5D=component" className={classes.all}>
          All components →
        </Link>
      </div>
      {opened === 'loading' && (
        <div className={classes.said}>
          <CircularProgress size={14} />
        </div>
      )}
      {opened === 'failed' && (
        <Typography className={classes.said}>
          Couldn't read what you opened last.
        </Typography>
      )}
      {Array.isArray(opened) && opened.length === 0 && (
        <Typography className={classes.said} data-state="empty">
          Nothing opened yet. The components you open appear here, newest first
          — in this browser.
        </Typography>
      )}
      {Array.isArray(opened) && opened.length > 0 && (
        <table className={classes.table} aria-label="Recently used">
          <thead>
            <tr>
              <th>Component</th>
              <th className={`${classes.colOwner} ${classes.wide}`}>Owner</th>
              <th className={`${classes.colLifecycle} ${classes.wide}`}>
                Lifecycle
              </th>
              <th className={classes.colCi}>CI</th>
              <th className={classes.colOpened}>Last opened</th>
            </tr>
          </thead>
          <tbody>
            {opened.map(({ row, openedAt }) => (
              <tr key={row.ref}>
                <td>
                  <Link
                    to={`/catalog/${row.namespace}/component/${row.name}`}
                    className={classes.service}
                  >
                    {row.title}
                  </Link>
                  {row.description && (
                    <span
                      className={classes.description}
                      title={row.description}
                    >
                      {row.description}
                    </span>
                  )}
                </td>
                <td className={classes.wide}>
                  <OwnerOf row={row} />
                </td>
                <td className={classes.wide}>
                  <LifecycleOf row={row} />
                </td>
                <td>
                  <CiCell slug={slugOf(row.entity)} />
                </td>
                <td className={classes.colOpened}>
                  <span
                    className={classes.opened}
                    title={new Date(openedAt).toLocaleString()}
                  >
                    {updatedLabel(new Date(openedAt).toISOString())}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};
